/**
 * インタラクティブキャンバス。
 *
 * - 基準解像度 = preset.width x preset.height（要素座標はこの解像度の px）
 * - 表示は CSS transform: scale で 1 つのサイズに収める
 * - クリックで要素選択（layerOrder 上で前面のもの優先 = 先頭側）
 * - ドラッグで移動
 * - 8 ハンドルでリサイズ
 * - テキストはダブルクリックで contenteditable に入り、即時反映
 * - 出力用に data-export-target を付与
 */
import {
  findAdVectorShape,
  type AdElement,
  type AdImageSlide,
  type AdPathElement,
  type AdSizePreset,
  type AdImagePlaceholderElement,
  type AdTextElement,
  type AdVectorElement,
  type PathPoint,
} from '@goenhub/shared';

/** 背景画像レイヤーの予約 ID */
export const BACKGROUND_LAYER_ID = '__background__';
import { Upload } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AdImagePickerDialog } from './AdImagePickerDialog';

interface Props {
  slide: AdImageSlide;
  preset: AdSizePreset;
  selectedId: string | null;
  /** Phase 38.3: 選択中の要素 ID 配列（複数選択。selectedId はその先頭との互換） */
  selectedIds?: string[];
  /** Phase 38.4: グループ編集モード中の groupId（指定された group の中身を個別選択可） */
  activeGroupId?: string | null;
  /** Phase 32.4 / 37: 現在のツール */
  tool?: 'cursor' | 'pen' | 'grab';
  /** Phase 38.3: クリックで選択。shift 修飾でトグル */
  onSelectElement: (id: string | null, opts?: { shift?: boolean }) => void;
  onPatchElement: (id: string, patch: Partial<AdElement>) => void;
  /** Phase 28: 背景画像の位置・サイズパッチ */
  onPatchBackground?: (patch: { x?: number; y?: number; w?: number; h?: number }) => void;
  /** Phase 28.2: スナップ機能を有効にするか */
  snapEnabled?: boolean;
  /**
   * Phase 32.3: Option+drag で複製。掴んだ要素を含むまとまり（グループは丸ごと）を元の位置に
   * 複製して残す。ドラッグで動くのは元の要素（選択は変えない）
   */
  onDuplicateForDrag?: (elementId: string) => void;
  /** Phase 32.5: ペンツールで新規 path 要素を追加 */
  onAddElement?: (el: AdElement) => void;
  /** Phase 33.1: ドラッグ等の連続操作で履歴を 1 ステップにまとめるための pause/resume */
  onBeginInteraction?: () => void;
  onEndInteraction?: () => void;
  /** Phase 36: ユーザー指定のズーム倍率 (0.25 - 2.0)、未指定なら 1 */
  zoom?: number;
  /** Phase 36: ピンチで変えたズーム値を親に通知 */
  onZoomChange?: (zoom: number) => void;
}

/** スナップで表示するガイドライン（描画用） */
interface SnapGuide {
  /** 'vertical' = 縦線（x 固定）、'horizontal' = 横線（y 固定） */
  axis: 'vertical' | 'horizontal';
  /** 基準解像度上の位置 (px) */
  pos: number;
}

const SNAP_THRESHOLD_PX = 8;

const MAX_DISPLAY_WIDTH = 720;
const MAX_DISPLAY_HEIGHT = 720;

export function InteractiveCanvas({
  slide,
  preset,
  selectedId,
  selectedIds,
  activeGroupId,
  tool = 'cursor',
  onSelectElement,
  onPatchElement,
  onPatchBackground,
  onDuplicateForDrag,
  onAddElement,
  onBeginInteraction,
  onEndInteraction,
  zoom = 1,
  onZoomChange,
  snapEnabled = true,
}: Props) {
  const selectedSet = useMemo(
    () =>
      new Set(selectedIds && selectedIds.length > 0 ? selectedIds : selectedId ? [selectedId] : []),
    [selectedIds, selectedId],
  );
  // Phase 38.3 / 38.4: 複数選択 + グループでのドラッグ用に、開始時のすべての位置をキャプチャ
  const multiDragStartsRef = useRef<Array<{ id: string; x: number; y: number }>>([]);
  // Phase 32.5: 描画中のパス（ペンツールで追加中の要素 ID）
  const [pendingPathId, setPendingPathId] = useState<string | null>(null);
  // ツールが変わったら描画中の path を確定
  useEffect(() => {
    if (tool !== 'pen') setPendingPathId(null);
  }, [tool]);
  // スナップガイドライン（ドラッグ中のみ表示）
  const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);
  // Phase 36: 「画面に収まる基準スケール」× ユーザーズーム倍率
  const baseScale = useMemo(() => {
    const sx = MAX_DISPLAY_WIDTH / preset.width;
    const sy = MAX_DISPLAY_HEIGHT / preset.height;
    return Math.min(sx, sy, 1);
  }, [preset]);
  const scale = baseScale * zoom;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);

  // Phase 36: トラックパッドピンチ (wheel + ctrlKey) で zoom 変更
  useEffect(() => {
    const el = outerRef.current;
    if (!el || !onZoomChange) return;
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey) return; // 通常スクロールは許可
      e.preventDefault();
      // deltaY の符号: 上 (拡大) で負、下 (縮小) で正。係数は感度調整
      const factor = 1 - e.deltaY * 0.01;
      const next = Math.max(0.25, Math.min(2, zoom * factor));
      onZoomChange(Number(next.toFixed(2)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [zoom, onZoomChange]);
  // layerOrder に従い、先頭ほど前面（描画順は後ほど）
  // クリックヒット判定では先頭から優先する
  const renderOrder = useMemo(() => {
    const lo = slide.layerOrder?.length ? slide.layerOrder : slide.elements.map((e) => e.id);
    const map = new Map(slide.elements.map((e) => [e.id, e]));
    // 表示は後ろ→前なので reverse する
    return [...lo]
      .reverse()
      .map((id) => map.get(id))
      .filter((e): e is AdElement => Boolean(e));
  }, [slide]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    // Phase 32.5: ペンツール時はクリックで anchor 追加
    if (tool === 'pen' && wrapperRef.current && onAddElement) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const localX = (e.clientX - rect.left) / scale;
      const localY = (e.clientY - rect.top) / scale;

      if (!pendingPathId) {
        // 新規 path 開始: 要素自体は座標 0,0 / w=preset.width, h=preset.height とし
        // points をローカル座標で持つ（描画は SVG）。回転・スケールは AdElementBase に任せる。
        const newId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const path: AdPathElement = {
          id: newId,
          kind: 'path',
          x: 0,
          y: 0,
          w: preset.width,
          h: preset.height,
          zIndex: 20,
          visible: true,
          locked: false,
          points: [{ x: localX, y: localY, cpInX: 0, cpInY: 0, cpOutX: 0, cpOutY: 0 }],
          closed: false,
          fill: 'none',
          stroke: '#111111',
          strokeWidth: 3,
          opacity: 1,
        };
        onAddElement(path);
        setPendingPathId(newId);
        onSelectElement(newId);
      } else {
        // 既存 path に anchor 追加 or 始点クリックで closed
        const path = slide.elements.find((el) => el.id === pendingPathId && el.kind === 'path') as
          | AdPathElement
          | undefined;
        if (path) {
          // Phase 35.3: 始点付近（12px 以内）クリックで closed にして path 確定
          const CLOSE_THRESHOLD = 12;
          const first = path.points[0];
          if (
            path.points.length >= 3 &&
            first &&
            Math.hypot(localX - first.x, localY - first.y) <= CLOSE_THRESHOLD
          ) {
            onPatchElement(pendingPathId, { closed: true } as Partial<AdElement>);
            setPendingPathId(null);
          } else {
            const next: PathPoint = {
              x: localX,
              y: localY,
              cpInX: 0,
              cpInY: 0,
              cpOutX: 0,
              cpOutY: 0,
            };
            onPatchElement(pendingPathId, { points: [...path.points, next] } as Partial<AdElement>);
          }
        }
      }
      return;
    }
    // 通常: 余白クリックで選択解除
    onSelectElement(null);
    // ペンツール中の path を確定（次のクリックは新規 path）
    setPendingPathId(null);
  };

  return (
    <div
      ref={outerRef}
      style={{ width: preset.width * scale, height: preset.height * scale }}
      className="relative shadow-lg ring-1 ring-[var(--border)] bg-white"
    >
      <div
        ref={wrapperRef}
        data-export-target={slide.id}
        style={{
          width: preset.width,
          height: preset.height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
        className="relative origin-top-left overflow-hidden bg-white"
        onMouseDown={handleBackgroundClick}
      >
        {/* 背景: crossOrigin を付けると Firebase Storage の CORS 制限で 0×0 になる。
            export は exportImage.ts 側で SDK 経由で data URL 化するため、ここでは付けない。 */}
        {slide.background?.url ? (
          <BackgroundLayer
            background={slide.background}
            preset={preset}
            scale={scale}
            selected={selectedId === BACKGROUND_LAYER_ID}
            tool={tool}
            onSelect={() => onSelectElement(BACKGROUND_LAYER_ID)}
            onPatch={onPatchBackground}
            onBeginInteraction={onBeginInteraction}
            onEndInteraction={onEndInteraction}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#f3f4f6] to-[#e5e7eb]" />
        )}

        {/* 要素 */}
        {renderOrder.map((el) => {
          const isSelected = selectedSet.has(el.id);
          // Phase 38.4: グループ編集モード中、別グループ or グループ外の要素はフェード
          const fadeOut = activeGroupId != null && (el.groupId ?? null) !== activeGroupId;
          return (
            <ElementView
              key={el.id}
              element={el}
              selected={isSelected}
              fadeOut={fadeOut}
              scale={scale}
              preset={preset}
              tool={tool}
              otherElements={snapEnabled ? slide.elements.filter((e) => e.id !== el.id) : []}
              snapEnabled={snapEnabled}
              onSnapGuidesChange={setSnapGuides}
              onSelect={(opts) => onSelectElement(el.id, opts)}
              onPatch={(patch) => onPatchElement(el.id, patch)}
              onBeginInteraction={() => {
                // Phase 38.3 / 38.4: ドラッグ開始時、一緒に動かす他要素の開始位置をキャプチャ。
                // 未選択のグループ要素をいきなり掴んだ場合（選択の反映がこの時点ではまだ）も
                // グループ全体を動かす（2026-09-25〜。以前は掴んだ 1 要素だけが動いた）
                const moveIds = selectedSet.has(el.id)
                  ? selectedSet
                  : new Set(
                      el.groupId && el.groupId !== activeGroupId
                        ? slide.elements.filter((o) => o.groupId === el.groupId).map((o) => o.id)
                        : [el.id],
                    );
                multiDragStartsRef.current =
                  moveIds.size > 1
                    ? slide.elements
                        .filter((other) => other.id !== el.id && moveIds.has(other.id))
                        .map((other) => ({ id: other.id, x: other.x, y: other.y }))
                    : [];
                onBeginInteraction?.();
              }}
              onEndInteraction={() => {
                multiDragStartsRef.current = [];
                onEndInteraction?.();
              }}
              onDragMove={(totalDx, totalDy) => {
                for (const start of multiDragStartsRef.current) {
                  onPatchElement(start.id, {
                    x: Math.round(start.x + totalDx),
                    y: Math.round(start.y + totalDy),
                  });
                }
              }}
              onRequestDuplicate={() => {
                onDuplicateForDrag?.(el.id);
              }}
            />
          );
        })}
        {/* スナップガイドライン */}
        {snapGuides.map((g, idx) => (
          <div
            key={idx}
            style={{
              position: 'absolute',
              pointerEvents: 'none',
              backgroundColor: '#FF3B30',
              zIndex: 200,
              ...(g.axis === 'vertical'
                ? { left: g.pos, top: 0, width: 1, height: preset.height }
                : { top: g.pos, left: 0, height: 1, width: preset.width }),
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface ElementViewProps {
  element: AdElement;
  selected: boolean;
  /** Phase 38.4: グループ編集モード中の他要素は半透明 */
  fadeOut?: boolean;
  scale: number;
  preset: AdSizePreset;
  otherElements: AdElement[];
  snapEnabled: boolean;
  /** Phase 33.2 / 37: ペン or グラブツール中は要素選択・ドラッグを無効化 */
  tool: 'cursor' | 'pen' | 'grab';
  onSnapGuidesChange: (guides: SnapGuide[]) => void;
  /** Phase 38.3: クリック選択。shift 修飾でトグル */
  onSelect: (opts?: { shift?: boolean }) => void;
  /** Phase 38.3: ドラッグ中、ドラッグ開始からの累計 delta を通知（複数選択の他要素を動かすため） */
  onDragMove?: (totalDx: number, totalDy: number) => void;
  /** Phase 32.3: Option+drag 開始時に複製を要求（複製は元の位置に残り、元の要素がドラッグで動く） */
  onRequestDuplicate?: () => void;
  /** Phase 33.1: drag を 1 ステップとしてまとめるための pause/resume */
  onBeginInteraction?: () => void;
  onEndInteraction?: () => void;
  onPatch: (patch: Partial<AdElement>) => void;
}

/**
 * スナップ計算: 移動中の要素の x/y/w/h と、他要素・キャンバス基準の整列線を比較し、
 * 閾値内なら吸着位置と表示ガイドラインを返す。
 */
function calcSnap(
  element: { x: number; y: number; w: number; h: number; id?: string },
  others: AdElement[],
  preset: AdSizePreset,
): { x: number; y: number; guides: SnapGuide[] } {
  const guides: SnapGuide[] = [];
  let bestDx = SNAP_THRESHOLD_PX + 1;
  let bestDy = SNAP_THRESHOLD_PX + 1;
  let snappedX = element.x;
  let snappedY = element.y;

  // 候補となる x 座標（垂直線）
  const vCandidates: Array<{ pos: number; type: 'el_left' | 'el_center' | 'el_right' }> = [
    { pos: 0, type: 'el_left' },
    { pos: preset.width / 2, type: 'el_center' },
    { pos: preset.width, type: 'el_right' },
  ];
  for (const o of others) {
    vCandidates.push(
      { pos: o.x, type: 'el_left' },
      { pos: o.x + o.w / 2, type: 'el_center' },
      { pos: o.x + o.w, type: 'el_right' },
    );
  }
  const myXAnchors = [
    { val: element.x, label: 'left' as const },
    { val: element.x + element.w / 2, label: 'center' as const },
    { val: element.x + element.w, label: 'right' as const },
  ];
  for (const c of vCandidates) {
    for (const anchor of myXAnchors) {
      const diff = c.pos - anchor.val;
      if (Math.abs(diff) < Math.abs(bestDx)) {
        bestDx = diff;
        snappedX = element.x + diff;
      }
    }
  }
  if (Math.abs(bestDx) <= SNAP_THRESHOLD_PX) {
    // 該当ガイドラインを記録
    const finalAnchorPos =
      Math.abs(snappedX - element.x) < 0.01 ? snappedX : snappedX + element.w / 2;
    void finalAnchorPos;
    // 簡易: スナップ後の x（要素のいずれかのアンカー）に近い候補を 1 つ抽出
    for (const c of vCandidates) {
      if (
        Math.abs(c.pos - snappedX) <= SNAP_THRESHOLD_PX ||
        Math.abs(c.pos - (snappedX + element.w / 2)) <= SNAP_THRESHOLD_PX ||
        Math.abs(c.pos - (snappedX + element.w)) <= SNAP_THRESHOLD_PX
      ) {
        guides.push({ axis: 'vertical', pos: c.pos });
      }
    }
  } else {
    snappedX = element.x;
  }

  // 候補となる y 座標（水平線）
  const hCandidates: Array<{ pos: number; type: 'el_top' | 'el_middle' | 'el_bottom' }> = [
    { pos: 0, type: 'el_top' },
    { pos: preset.height / 2, type: 'el_middle' },
    { pos: preset.height, type: 'el_bottom' },
  ];
  for (const o of others) {
    hCandidates.push(
      { pos: o.y, type: 'el_top' },
      { pos: o.y + o.h / 2, type: 'el_middle' },
      { pos: o.y + o.h, type: 'el_bottom' },
    );
  }
  const myYAnchors = [
    { val: element.y, label: 'top' as const },
    { val: element.y + element.h / 2, label: 'middle' as const },
    { val: element.y + element.h, label: 'bottom' as const },
  ];
  for (const c of hCandidates) {
    for (const anchor of myYAnchors) {
      const diff = c.pos - anchor.val;
      if (Math.abs(diff) < Math.abs(bestDy)) {
        bestDy = diff;
        snappedY = element.y + diff;
      }
    }
  }
  if (Math.abs(bestDy) <= SNAP_THRESHOLD_PX) {
    for (const c of hCandidates) {
      if (
        Math.abs(c.pos - snappedY) <= SNAP_THRESHOLD_PX ||
        Math.abs(c.pos - (snappedY + element.h / 2)) <= SNAP_THRESHOLD_PX ||
        Math.abs(c.pos - (snappedY + element.h)) <= SNAP_THRESHOLD_PX
      ) {
        guides.push({ axis: 'horizontal', pos: c.pos });
      }
    }
  } else {
    snappedY = element.y;
  }
  return { x: Math.round(snappedX), y: Math.round(snappedY), guides };
}

function ElementView({
  element,
  selected,
  fadeOut,
  scale,
  preset,
  tool,
  otherElements,
  snapEnabled,
  onSnapGuidesChange,
  onSelect,
  onPatch,
  onDragMove,
  onRequestDuplicate,
  onBeginInteraction,
  onEndInteraction,
}: ElementViewProps) {
  const [isEditingText, setIsEditingText] = useState(false);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
    mode: 'move' | 'resize';
    handle?: ResizeHandle;
  } | null>(null);

  const startDrag = useCallback(
    (e: React.MouseEvent, mode: 'move' | 'resize', handle?: ResizeHandle) => {
      if (element.locked) return;
      // Phase 33.2 / 37: ペン or グラブツール中は要素ドラッグ無効
      if (tool === 'pen' || tool === 'grab') return;
      e.stopPropagation();
      // Phase 38.3: 選択ロジックを mousedown 一箇所に集約（onClick との二重発火回避）
      // - shift+クリック: 常にトグル
      // - 通常クリック (未選択): 置き換え
      // - 通常クリック (選択中): 何もしない（ドラッグで他選択を保持）
      // リサイズハンドルは選択中の要素にしか出ないので、Shift（比率固定）で選択を外さない
      if (mode === 'move' && e.shiftKey) {
        onSelect({ shift: true });
      } else if (!selected) {
        onSelect({ shift: false });
      }
      if (mode === 'move' && e.altKey && onRequestDuplicate) {
        onRequestDuplicate();
      }
      onBeginInteraction?.();
      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: element.x,
        origY: element.y,
        origW: element.w,
        origH: element.h,
        mode,
        handle,
      };
      const onMove = (ev: MouseEvent) => {
        const s = dragStateRef.current;
        if (!s) return;
        const dx = (ev.clientX - s.startX) / scale;
        const dy = (ev.clientY - s.startY) / scale;
        if (s.mode === 'move') {
          let nextX = Math.round(s.origX + dx);
          let nextY = Math.round(s.origY + dy);
          if (snapEnabled) {
            const snap = calcSnap(
              { x: nextX, y: nextY, w: s.origW, h: s.origH, id: element.id },
              otherElements,
              preset,
            );
            nextX = snap.x;
            nextY = snap.y;
            onSnapGuidesChange(snap.guides);
          }
          onPatch({ x: nextX, y: nextY });
          // Phase 38.3 / 38.4: 複数選択中の他要素も同じ delta で動かす
          onDragMove?.(nextX - s.origX, nextY - s.origY);
        } else {
          lastDelta = { dx, dy };
          onPatch(applyResize(s, dx, dy, { keepRatio: ev.shiftKey, fromCenter: ev.altKey }));
        }
      };
      // リサイズ中に Shift / Option を押す・離すだけでも（マウスを動かさなくても）反映する
      let lastDelta = { dx: 0, dy: 0 };
      const onKey = (ev: KeyboardEvent) => {
        const s = dragStateRef.current;
        if (!s || s.mode !== 'resize') return;
        if (ev.key !== 'Shift' && ev.key !== 'Alt') return;
        ev.preventDefault();
        onPatch(
          applyResize(s, lastDelta.dx, lastDelta.dy, {
            keepRatio: ev.shiftKey,
            fromCenter: ev.altKey,
          }),
        );
      };
      const onUp = () => {
        dragStateRef.current = null;
        onSnapGuidesChange([]);
        // Phase 33.1: ドラッグ確定で履歴を 1 ステップに確定
        onEndInteraction?.();
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('keydown', onKey);
        window.removeEventListener('keyup', onKey);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      if (mode === 'resize') {
        window.addEventListener('keydown', onKey);
        window.addEventListener('keyup', onKey);
      }
    },
    [
      element.locked,
      tool,
      element.x,
      element.y,
      element.w,
      element.h,
      onPatch,
      onSelect,
      onRequestDuplicate,
      onBeginInteraction,
      onEndInteraction,
      scale,
    ],
  );

  if (!element.visible) return null;

  // Phase 31: rotation + flipX/flipY を transform に合成
  const transforms: string[] = [];
  if (element.rotation) transforms.push(`rotate(${element.rotation}deg)`);
  if (element.flipX) transforms.push('scaleX(-1)');
  if (element.flipY) transforms.push('scaleY(-1)');
  const style: React.CSSProperties = {
    position: 'absolute',
    left: element.x,
    top: element.y,
    width: element.w,
    height: element.h,
    transform: transforms.length > 0 ? transforms.join(' ') : undefined,
    cursor: element.locked ? 'default' : isEditingText ? 'text' : 'move',
  };

  return (
    <div
      style={{
        ...style,
        // Phase 33.2 / 37: ペン or グラブツール中は要素クリック透過
        ...(tool === 'pen' || tool === 'grab' ? { pointerEvents: 'none' as const } : {}),
        // Phase 38.4: グループ編集モード中の非対象要素はフェード
        ...(fadeOut ? { opacity: 0.3 } : {}),
      }}
      onMouseDown={(e) => {
        if (isEditingText) return;
        if (tool === 'pen' || tool === 'grab') return;
        startDrag(e, 'move');
      }}
      onClick={(e) => {
        if (tool === 'pen' || tool === 'grab') return;
        // Phase 38.3: 選択は mousedown で処理済み。stopPropagation で親伝搬は止める
        e.stopPropagation();
      }}
      data-element-frame
      className={`${selected ? 'outline outline-2 outline-[var(--accent)]' : ''}`}
    >
      {element.kind === 'text' ? (
        <TextElementRender
          element={element}
          isEditing={isEditingText}
          onStartEdit={() => setIsEditingText(true)}
          onEndEdit={(content) => {
            setIsEditingText(false);
            if (content !== element.content) onPatch({ content });
          }}
        />
      ) : element.kind === 'vector' ? (
        <VectorElementRender element={element} />
      ) : element.kind === 'path' ? (
        <PathElementRender
          element={element}
          selected={selected}
          tool={tool}
          onPatch={onPatch}
          onBeginInteraction={onBeginInteraction}
          onEndInteraction={onEndInteraction}
        />
      ) : (
        <ImagePlaceholderRender element={element} onPatch={onPatch} />
      )}

      {/* リサイズハンドル */}
      {selected && !element.locked && !isEditingText && (
        <>
          {(['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as ResizeHandle[]).map((h) => (
            <ResizeHandleDot key={h} handle={h} onMouseDown={(e) => startDrag(e, 'resize', h)} />
          ))}
          {/* 角丸 R ハンドル（rect_rounded のみ） */}
          {element.kind === 'vector' && element.shape === 'rect_rounded' && (
            <CornerRadiusHandle
              element={element}
              scale={scale}
              onPatch={(patch) => onPatch(patch)}
            />
          )}
        </>
      )}
    </div>
  );
}

function CornerRadiusHandle({
  element,
  scale,
  onPatch,
}: {
  element: AdVectorElement;
  scale: number;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  const defaultR = Math.round(Math.min(element.w, element.h) / 4);
  const r = element.cornerRadius ?? defaultR;
  const maxR = Math.min(element.w, element.h) / 2;
  // ハンドルは左上角から内側へ r 分入った位置
  const handleX = Math.max(8, Math.min(r, maxR));
  const handleY = handleX;

  const startDrag = (e: React.MouseEvent) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startR = r;
    const onMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / scale;
      const newR = Math.max(0, Math.min(maxR, startR + dx));
      onPatch({ cornerRadius: Math.round(newR) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  return (
    <div
      onMouseDown={startDrag}
      style={{
        position: 'absolute',
        left: handleX - 7,
        top: handleY - 7,
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: '#FFB020',
        border: '2px solid white',
        cursor: 'ew-resize',
        zIndex: 110,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
      }}
      title={`角丸 R: ${r}px`}
    />
  );
}

function TextElementRender({
  element,
  isEditing,
  onStartEdit,
  onEndEdit,
}: {
  element: AdTextElement;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: (content: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  // contentEditable は完全に DOM 主導（React の children では描画しない）。
  // React に子を持たせると編集開始（isEditing 切替）時に text node が消され、
  // 元の値がクリアされてしまうため。編集中でない時のみ element.content を同期する。
  // useLayoutEffect でペイント前に流し込み、初回マウントのちらつきを防ぐ。
  useLayoutEffect(() => {
    if (!ref.current) return;
    if (!isEditing && ref.current.innerText !== element.content) {
      ref.current.innerText = element.content;
    }
  }, [element.content, isEditing]);

  const padding = element.padding ?? 0;
  const baseStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    padding,
    fontFamily: element.fontFamily,
    fontSize: element.fontSize,
    fontWeight: element.fontWeight,
    color: element.color,
    textAlign: element.align,
    lineHeight: element.lineHeight ?? 1.2,
    letterSpacing: element.letterSpacing ?? 0,
    background: element.background ?? 'transparent',
    textShadow: element.shadow
      ? `${element.shadow.x}px ${element.shadow.y}px ${element.shadow.blur}px ${element.shadow.color}`
      : undefined,
    outline: 'none',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      element.align === 'left' ? 'flex-start' : element.align === 'right' ? 'flex-end' : 'center',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    userSelect: isEditing ? 'text' : 'none',
  };

  // テキストは useLayoutEffect で DOM へ直接流し込む（React children は持たない）。
  // children を持たせると編集開始時に text node が消え、元の値がクリアされるため。
  return (
    <div
      ref={ref}
      style={baseStyle}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onDoubleClick={(e) => {
        e.stopPropagation();
        // 編集開始時、念のため DOM に現在値が入っていることを保証してから全選択
        if (ref.current && ref.current.innerText !== element.content) {
          ref.current.innerText = element.content;
        }
        onStartEdit();
        requestAnimationFrame(() => {
          ref.current?.focus();
          const range = document.createRange();
          range.selectNodeContents(ref.current!);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        });
      }}
      onBlur={(e) => {
        const next = e.currentTarget.innerText;
        onEndEdit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') (e.target as HTMLDivElement).blur();
      }}
    />
  );
}

function BackgroundLayer({
  background,
  preset,
  scale,
  selected,
  tool,
  onSelect,
  onPatch,
  onBeginInteraction,
  onEndInteraction,
}: {
  background: NonNullable<AdImageSlide['background']>;
  preset: AdSizePreset;
  scale: number;
  selected: boolean;
  tool?: 'cursor' | 'pen' | 'grab';
  onSelect: () => void;
  onPatch?: (patch: { x?: number; y?: number; w?: number; h?: number }) => void;
  onBeginInteraction?: () => void;
  onEndInteraction?: () => void;
}) {
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
    mode: 'move' | 'resize';
    handle?: ResizeHandle;
  } | null>(null);

  const hasCustomLayout =
    background.x !== undefined ||
    background.y !== undefined ||
    background.w !== undefined ||
    background.h !== undefined;
  const x = background.x ?? 0;
  const y = background.y ?? 0;
  const w = background.w ?? preset.width;
  const h = background.h ?? preset.height;
  const fit = background.fit ?? 'cover';
  const opacity = background.opacity ?? 1;

  const startDrag = (e: React.MouseEvent, mode: 'move' | 'resize', handle?: ResizeHandle) => {
    if (!onPatch) return;
    if (tool === 'pen' || tool === 'grab') return;
    e.stopPropagation();
    onSelect();
    onBeginInteraction?.();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: x,
      origY: y,
      origW: w,
      origH: h,
      mode,
      handle,
    };
    const onMove = (ev: MouseEvent) => {
      const s = dragRef.current;
      if (!s) return;
      const dx = (ev.clientX - s.startX) / scale;
      const dy = (ev.clientY - s.startY) / scale;
      if (s.mode === 'move') {
        onPatch({ x: Math.round(s.origX + dx), y: Math.round(s.origY + dy) });
      } else {
        lastDelta = { dx, dy };
        onPatch(applyResize(s, dx, dy, { keepRatio: ev.shiftKey, fromCenter: ev.altKey }));
      }
    };
    // リサイズ中の Shift / Option の押下・解除にも追従（要素と同じ）
    let lastDelta = { dx: 0, dy: 0 };
    const onKey = (ev: KeyboardEvent) => {
      const s = dragRef.current;
      if (!s || s.mode !== 'resize') return;
      if (ev.key !== 'Shift' && ev.key !== 'Alt') return;
      ev.preventDefault();
      onPatch(
        applyResize(s, lastDelta.dx, lastDelta.dy, {
          keepRatio: ev.shiftKey,
          fromCenter: ev.altKey,
        }),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      onEndInteraction?.();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    if (mode === 'resize') {
      window.addEventListener('keydown', onKey);
      window.addEventListener('keyup', onKey);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: w,
        height: h,
        opacity,
        cursor: onPatch && tool !== 'pen' && tool !== 'grab' ? 'move' : 'default',
        // Phase 33.2 / 37: ペン or グラブ時はクリック透過
        ...(tool === 'pen' || tool === 'grab' ? { pointerEvents: 'none' as const } : {}),
      }}
      onMouseDown={(e) => {
        if (tool === 'pen' || tool === 'grab') return;
        if (onPatch) startDrag(e, 'move');
      }}
      onClick={(e) => {
        if (tool === 'pen' || tool === 'grab') return;
        e.stopPropagation();
        onSelect();
      }}
      className={`${selected ? 'outline outline-2 outline-[var(--accent)]' : ''}`}
      data-element-frame
    >
      <img
        src={background.url}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: fit,
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {selected && onPatch && (
        <>
          {(['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as ResizeHandle[]).map((h2) => (
            <ResizeHandleDot key={h2} handle={h2} onMouseDown={(e) => startDrag(e, 'resize', h2)} />
          ))}
        </>
      )}
      {/* hasCustomLayout 未使用警告対策 */}
      <span style={{ display: 'none' }}>{hasCustomLayout ? 'custom' : 'default'}</span>
    </div>
  );
}

/**
 * Phase 32.5: ペンツールで描画されたパスのレンダラ
 * - SVG で <path d="M ... C ... C ..."> を組み立てる
 * - 選択中は anchor / handle にレバー UI を表示し、ドラッグで編集可能
 */
function PathElementRender({
  element,
  selected,
  tool,
  onPatch,
  onBeginInteraction,
  onEndInteraction,
}: {
  element: AdPathElement;
  selected: boolean;
  tool: 'cursor' | 'pen' | 'grab';
  onPatch: (patch: Partial<AdElement>) => void;
  onBeginInteraction?: () => void;
  onEndInteraction?: () => void;
}) {
  // Phase 38.2: ペンツール時は anchor / handle のドラッグを無効化（canvas のペン処理に委譲）
  // → 始点クリックが PathElementRender ではなく InteractiveCanvas.handleBackgroundClick に到達し、
  //   閉じる判定が動作する
  const anchorPointerEvents = tool === 'pen' ? 'none' : 'auto';
  const w = element.w;
  const h = element.h;
  const points = element.points;

  // SVG path d="" を組み立てる
  const d = useMemo(() => {
    if (points.length === 0) return '';
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const cur = points[i];
      const c1x = prev.x + prev.cpOutX;
      const c1y = prev.y + prev.cpOutY;
      const c2x = cur.x + cur.cpInX;
      const c2y = cur.y + cur.cpInY;
      path += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${cur.x} ${cur.y}`;
    }
    if (element.closed) path += ' Z';
    return path;
  }, [points, element.closed]);

  const onAnchorDrag = (idx: number, key: 'anchor' | 'in' | 'out') => (ev: React.MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    // Phase 35.4: ドラッグ全体を 1 履歴ステップにまとめる
    onBeginInteraction?.();
    const startX = ev.clientX;
    const startY = ev.clientY;
    const start = { ...points[idx] };
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const next = [...points];
      if (key === 'anchor') {
        next[idx] = { ...start, x: start.x + dx, y: start.y + dy };
      } else if (key === 'in') {
        next[idx] = { ...start, cpInX: start.cpInX + dx, cpInY: start.cpInY + dy };
      } else {
        next[idx] = { ...start, cpOutX: start.cpOutX + dx, cpOutY: start.cpOutY + dy };
      }
      onPatch({ points: next } as Partial<AdElement>);
    };
    const onUp = () => {
      onEndInteraction?.();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}
    >
      <path
        d={d}
        fill={element.fill === 'none' ? 'none' : element.fill}
        stroke={element.stroke === 'none' ? 'none' : element.stroke}
        strokeWidth={element.strokeWidth}
        opacity={element.opacity ?? 1}
        vectorEffect="non-scaling-stroke"
      />
      {selected &&
        points.map((p, i) => {
          const showHandles = p.cpInX !== 0 || p.cpInY !== 0 || p.cpOutX !== 0 || p.cpOutY !== 0;
          // Phase 38.2: ペンツールで開いた path の始点には close 候補としてリングを表示
          const isCloseTarget = tool === 'pen' && !element.closed && i === 0 && points.length >= 3;
          return (
            <g key={i}>
              {isCloseTarget && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={10}
                  fill="none"
                  stroke="#FF8800"
                  strokeWidth={1.5}
                  strokeDasharray="2 2"
                />
              )}
              {showHandles && (
                <>
                  <line
                    x1={p.x}
                    y1={p.y}
                    x2={p.x + p.cpInX}
                    y2={p.y + p.cpInY}
                    stroke="#888"
                    strokeDasharray="3 3"
                  />
                  <line
                    x1={p.x}
                    y1={p.y}
                    x2={p.x + p.cpOutX}
                    y2={p.y + p.cpOutY}
                    stroke="#888"
                    strokeDasharray="3 3"
                  />
                  <circle
                    cx={p.x + p.cpInX}
                    cy={p.y + p.cpInY}
                    r={5}
                    fill="#fff"
                    stroke="#0080FF"
                    strokeWidth={1.5}
                    style={{ pointerEvents: anchorPointerEvents, cursor: 'move' }}
                    onMouseDown={onAnchorDrag(i, 'in')}
                  />
                  <circle
                    cx={p.x + p.cpOutX}
                    cy={p.y + p.cpOutY}
                    r={5}
                    fill="#fff"
                    stroke="#0080FF"
                    strokeWidth={1.5}
                    style={{ pointerEvents: anchorPointerEvents, cursor: 'move' }}
                    onMouseDown={onAnchorDrag(i, 'out')}
                  />
                </>
              )}
              <rect
                x={p.x - 5}
                y={p.y - 5}
                width={10}
                height={10}
                fill="#fff"
                stroke="#0080FF"
                strokeWidth={2}
                style={{ pointerEvents: anchorPointerEvents, cursor: 'move' }}
                onMouseDown={onAnchorDrag(i, 'anchor')}
                onDoubleClick={(e) => {
                  // ダブルクリックで handle を生成（短い両側 handle）
                  e.stopPropagation();
                  const HANDLE = 30;
                  const next = [...points];
                  next[i] = { ...next[i], cpInX: -HANDLE, cpInY: 0, cpOutX: HANDLE, cpOutY: 0 };
                  onPatch({ points: next } as Partial<AdElement>);
                }}
              />
            </g>
          );
        })}
    </svg>
  );
}

function VectorElementRender({ element }: { element: AdVectorElement }) {
  // Phase 30: カスタム SVG が指定されていれば <img> で描画
  if (element.customSvgUrl) {
    return (
      <img
        src={element.customSvgUrl}
        alt={element.customSvgLabel ?? 'custom svg'}
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          opacity: element.opacity ?? 1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
    );
  }
  // rect_rounded は HTML div で描画して、border-radius を px 固定にし、
  // 縦横比が変わっても角の R が崩れないようにする
  if (element.shape === 'rect_rounded') {
    const defaultR = Math.round(Math.min(element.w, element.h) / 4);
    const r = element.cornerRadius ?? defaultR;
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: `${r}px`,
          backgroundColor: element.fill === 'none' ? 'transparent' : element.fill,
          border:
            element.stroke === 'none' ? 'none' : `${element.strokeWidth}px solid ${element.stroke}`,
          opacity: element.opacity ?? 1,
          pointerEvents: 'none',
        }}
      />
    );
  }
  // 通常の rect (角丸なし) も div で描画
  if (element.shape === 'rect') {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: element.fill === 'none' ? 'transparent' : element.fill,
          border:
            element.stroke === 'none' ? 'none' : `${element.strokeWidth}px solid ${element.stroke}`,
          opacity: element.opacity ?? 1,
          pointerEvents: 'none',
        }}
      />
    );
  }
  // それ以外は SVG で描画（複雑な path shape）
  const def = findAdVectorShape(element.shape);
  if (!def) return null;
  // Phase 30: strokeOnly のフレーム / 線画装飾は preserveAspectRatio を保持してアスペクト維持
  // （フレームの場合は枠の太さが歪まないように）
  const isStrokeOnly = def.strokeOnly === true;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio={isStrokeOnly ? 'none' : 'none'}
      style={{ display: 'block', opacity: element.opacity ?? 1, pointerEvents: 'none' }}
    >
      <path
        d={def.d}
        fill={isStrokeOnly ? 'none' : element.fill}
        stroke={
          isStrokeOnly
            ? element.stroke === 'none'
              ? element.fill
              : element.stroke
            : element.stroke
        }
        strokeWidth={isStrokeOnly && element.strokeWidth === 0 ? 2 : element.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function ImagePlaceholderRender({
  element,
  onPatch,
}: {
  element: AdImagePlaceholderElement;
  onPatch: (patch: Partial<AdElement>) => void;
}) {
  // ローカルアップロードではなく、プラットフォームにアップロード済みの画像を
  // 参照できる「画像を選択」モーダルを開く（背景レイヤー等と同じ体験）。
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <>
      {element.imageUrl ? (
        <div className="w-full h-full relative group" onDoubleClick={() => setPickerOpen(true)}>
          <img
            src={element.imageUrl}
            alt={element.label}
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: element.fit,
              borderRadius: element.borderRadius,
            }}
          />
        </div>
      ) : (
        <div
          data-image-placeholder
          className="w-full h-full border-2 border-dashed border-[var(--accent)] bg-[var(--bg-elevated)]/80 flex flex-col items-center justify-center cursor-pointer text-[var(--text-muted)] text-xs gap-2"
          onClick={() => setPickerOpen(true)}
          style={{ borderRadius: element.borderRadius }}
        >
          <Upload className="size-6" />
          <span>{`${element.label} 画像を挿入`}</span>
        </div>
      )}
      <AdImagePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(url) => onPatch({ imageUrl: url })}
      />
    </>
  );
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLE_STYLES: Record<ResizeHandle, React.CSSProperties> = {
  nw: { top: -6, left: -6, cursor: 'nwse-resize' },
  n: { top: -6, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' },
  ne: { top: -6, right: -6, cursor: 'nesw-resize' },
  e: { top: '50%', right: -6, cursor: 'ew-resize', transform: 'translateY(-50%)' },
  se: { bottom: -6, right: -6, cursor: 'nwse-resize' },
  s: { bottom: -6, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' },
  sw: { bottom: -6, left: -6, cursor: 'nesw-resize' },
  w: { top: '50%', left: -6, cursor: 'ew-resize', transform: 'translateY(-50%)' },
};

function ResizeHandleDot({
  handle,
  onMouseDown,
}: {
  handle: ResizeHandle;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        width: 12,
        height: 12,
        background: 'var(--accent)',
        border: '2px solid white',
        borderRadius: 2,
        zIndex: 100,
        ...HANDLE_STYLES[handle],
      }}
      onMouseDown={onMouseDown}
    />
  );
}

/**
 * 枠のリサイズ（要素・背景レイヤー共通）。変わるのは x / y / w / h だけで、中身は枠に合わせて描画される
 * （テキストは枠内で折返し、画像は fit 設定、ベクターは伸縮。文字サイズやパスの頂点は変えない）。
 * 各辺 20px 未満にはしない。回転は考慮しない（従来どおり）。
 *
 * 修飾キー（2026-09-25〜、ドラッグ中の押下・解除にも追従）:
 * - keepRatio（Shift）: 角ハンドルのみ、元の縦横比を保つ。幅・高さのうち変化の大きい方に合わせる
 * - fromCenter（Option / Alt）: 角・辺とも、要素の中心を固定して拡大縮小する（変化量は両側に振り分け）
 * 修飾キーなし: 掴んだハンドルの反対側を固定（従来どおり）
 */
function applyResize(
  s: { origX: number; origY: number; origW: number; origH: number; handle?: ResizeHandle },
  dx: number,
  dy: number,
  mods?: { keepRatio?: boolean; fromCenter?: boolean },
): Partial<AdElement> {
  const MIN = 20;
  const handle = s.handle ?? 'se';
  const signX = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0;
  const signY = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;
  const isCorner = signX !== 0 && signY !== 0;
  const fromCenter = !!mods?.fromCenter;
  const keepRatio = !!mods?.keepRatio && isCorner;
  // 中心固定では掴んだ側の移動量が反対側にも付くので、幅・高さの変化は 2 倍
  const factor = fromCenter ? 2 : 1;

  let w = signX !== 0 ? s.origW + signX * dx * factor : s.origW;
  let h = signY !== 0 ? s.origH + signY * dy * factor : s.origH;

  if (keepRatio) {
    const kx = w / s.origW;
    const ky = h / s.origH;
    // 変化の大きい方（1 から遠い方）に合わせる
    let k = Math.abs(kx - 1) >= Math.abs(ky - 1) ? kx : ky;
    // 比率を保ったまま、どちらの辺も最小値を下回らないようにする
    k = Math.max(k, MIN / s.origW, MIN / s.origH);
    w = s.origW * k;
    h = s.origH * k;
  } else {
    w = Math.max(MIN, w);
    h = Math.max(MIN, h);
  }

  let x = s.origX;
  let y = s.origY;
  if (fromCenter) {
    x = s.origX + (s.origW - w) / 2;
    y = s.origY + (s.origH - h) / 2;
  } else {
    // 反対側を固定: 左・上のハンドルを掴んだときは右・下端を固定
    if (signX === -1) x = s.origX + s.origW - w;
    if (signY === -1) y = s.origY + s.origH - h;
  }
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}
